import {IAppConfig} from './app-config/app_config';
import {LoginServiceEvents} from "./oe/login-service";
import {IUsageDataFull, IUsageLoggingService, LoggingEvent, Session} from "./IUsageLoggingService";
import {uuidv4} from "../utility/uuidv4";
import {setupLogPageChange} from "./LogPageChange";


export class StaticPageUsageLoggingService implements IUsageLoggingService {
	public static $inject = [
		'$http',
		'$rootScope',
		'$q',
		'$injector'
	];

	protected session: Session;
	protected logURL = '/usage/log';
	protected logSessionURL = '/usage/log_session';
	protected ipAddress : () => ng.IPromise<string>;
	protected _ip: string;

	get ip(): string {
		return this._ip;
	}

	protected _appConfig: IAppConfig;
	protected get appConfig() {
		return this._appConfig = this._appConfig || this.$injector.get('appConfig');
	}

	get application(): string {
		return this.appConfig.getApplication();
	}

	constructor(
		protected $http: ng.IHttpService,
		protected $rootScope: ng.IRootScopeService,
		protected $q: ng.IQService,
		protected $injector
	) {
		this.$rootScope.$on(LoginServiceEvents.LoggedIn, () => {
			this.initSession();
		});
		this.initSession();

		setupLogPageChange(this);

		let ipAddressRequest : ng.IPromise<string> = null;
        this.ipAddress = () => { // lazy loading singleton
            if (!ipAddressRequest) {
                // /Logging/clientAddress
				ipAddressRequest = $http.get('/Logging/clientAddress')
					.then((data) => {
                        console.log(`Got IP address from local`, data.data['ip']);
						return data.data['ip'];
					}, (/*err*/) => {
						// The request to ipify failed, likely blocked due to an ad-blocker
						return '';
					}).then(ip => {
						this._ip = ip;
						return ip;
					});
            }
			if (!ipAddressRequest) {
				ipAddressRequest = $http.get('https://api.ipify.org?format=json')
					.then((data) => {
						return data.data['ip'];
					}, (/*err*/) => {
						// The request to ipify failed, likely blocked due to an ad-blocker
						return '';
					}).then(ip => {
						this._ip = ip;
						return ip;
					});
			}
			return ipAddressRequest;
		};

		$rootScope.$on('setLocationCenter', (center, zoom) => {
			console.log(center, zoom);
			this.logAction('SCREEN_MAP');
		});
		window.addEventListener("LOG_EVENT", (ev: LoggingEvent) => {
			this.logWithAnalytics(
				ev.category,
				ev.action,
				ev.label,
				ev.scale,
				ev.layout
			)
		})
	}

	resetSession() {
		sessionStorage.removeItem('sessionId');
		// this.initSession();
	}

	/**
	 * get a new or existing session ID
	 * has side effect of storing in sessionStorage!
	 */
	getSessionUuid(): string {
		// get the session UUID from the server and only if not possible, generate a new one
		const config = (window as any).LTConfiguration
		if (!config || !config.sessionUuid) {
			return uuidv4();
		}
		return config.sessionUuid;
	}
	getDeviceUuid(): string {
		let deviceId = localStorage.getItem('sessionId'); // luckily it's in localStorage and session is in sessionStorage!
		if (deviceId) return deviceId;
		deviceId = uuidv4();
		localStorage.setItem('sessionId', deviceId);
		return deviceId;
	}
	initSession() {
		// const { email, license, organisation } = window['LTConfiguration'];
		let email: string = '';
		let license: string = '';
		let organisation: string = '';
		if ('LTConfiguration' in window) {
			if ('email' in window['LTConfiguration']) email = window['LTConfiguration']['email'];
			if ('license' in window['LTConfiguration']) license = window['LTConfiguration']['license'];
			if ('organisation' in window['LTConfiguration']) organisation = window['LTConfiguration']['organisation'];
		}
		this.session = {
			'session_id': this.getSessionUuid(),
			'device_id': this.getDeviceUuid(),
			'email_hash': email,
			'email_suffix': this.getEmailSuffix(email),
			organisation,
			license: JSON.stringify(license),
			'user_agent': navigator.userAgent,
		};
		return this.$http<any>({
			url: this.logSessionURL,
			method: "POST",
			transformRequest: this.transformRequest,
			data: this.session,
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
		}).catch((reason: any) => {
			const msg = `Logging Status: Code ${reason.status} Message: ${reason.statusText} URL: ${this.logURL}`;
			console.error(msg, reason);
			throw new Error(msg);
		});
	}

	transformRequest(obj) {
		let str = [];
		for (let p in obj)
			if (obj.hasOwnProperty(p)) {
				str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
			}
		return str.join("&");
	}
	getEmailSuffix(email: string): string {
		if (!email || !email.match(/@/)) return email;
		return email.substring(email.indexOf('@'));
	}

	/*
	public getDefaults(action : string) : ng.IPromise<IUsageData> {
		return this.ipAddress().then((ipAddress) => {
			return {
				application: this.application,
				action: action,
				layers: this.getLayersParam(),
				spatial_extent: this.getSpatialExtentParam(),
				ip: ipAddress
			};
		});
	}
*/

	public log(usage: IUsageDataFull): ng.IHttpPromise<string> {
		usage.session_uuid = this.session.session_id;
		usage.window_width = window.innerWidth;
		usage.window_height = window.innerHeight;

		const promise = this.$q.resolve().then(() => {
			return this.$http<any>({
				url: this.logURL,
				method: "POST",
				transformRequest: this.transformRequest,
				data: usage,
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
			}).catch((reason: any) => {
				const msg = `Logging Status: Code ${reason.status} Message: ${reason.statusText} URL: ${this.logURL}`;
				console.error(msg, reason);
				throw new Error(msg);
			});
		})
		promise.catch(reason => {
			this.$http<any>({
				url: this.logURL,
				method: "POST",
				transformRequest: this.transformRequest,
				data: usage,
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
			});
			console.log('log repeated because', reason);
		});
		return promise;
	}

	public logAction(action: string, extraInfo? : string, scale: string = null, layout: string = null) : ng.IHttpPromise<string> {
		return this.ipAddress().then((ipAddress) => {
			let data : IUsageDataFull = {
				application: this.application,
				action: action,
				layers: '',
				spatial_extent: extraInfo || 'unknown',
				ip: ipAddress
			};

			if (scale) data.scale = scale;
			if (layout) data.layout = layout;

			return this.log(data);
		}, () => {
			let data : IUsageDataFull = {
				application: this.application,
				action: action,
				layers: '',
				spatial_extent: extraInfo || 'unknown',
				ip: ''
			};

			if (scale) data.scale = scale;
			if (layout) data.layout = layout;

			return this.log(data);
		});
	}


	logWithAnalytics(category: string, action: string, label: string, scale: string = null, layout: string = null) {
		if ('gtag' in window) {
			const gtag = window['gtag'] as (event: string, eventName: string, eventParameters: any) => void;
			// console.log('gtag', 'event', category, { action, label, scale, layout, })
			// gtag('event', category, { action, label, scale, layout, });
			console.log('gtag event', category, label, action);
            gtag('event', category, {
                event_detail: action,
                event_label: label,
                value: 1
            });
		} else if ('ga' in window) {
			const ga = window['ga'] as (send: string, event: string, category: string, action: string, label: string) => void;
			ga('send', 'event', category, action, label);
		} else {
			console.log("Google Analytics", 'send', 'event', category, action, label);
		}
		this.logAction(category, [action == 'click' ? '' : action, label].filter((e) => e).join(', '), scale, layout);
	}
}
